'use client';

import React from 'react';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';

import HeroValdProd from '@src/components/Sections/HeroValdProd';
import RenderBodyCopy from '@src/components/Shared/RenderBodyCopy';
import RenderButtons from '@src/components/Shared/RenderButtons';
import Container from '@src/components/Shared/Container';
import SectionHeader from '@src/components/Shared/SectionHeader';

import HotSpots from '@src/components/Sections/HotSpots';
import ImageTextSecondary from '@src/components/Sections/ImageTextSecondary';
import FeaturedProducts from '@src/components/Sections/FeaturedProducts';
import CallToAction from '@src/components/Sections/CallToAction';
import HumanTrakVideo from '@src/components/Sections/HumanTrakVideo/HumanTrakVideo';

import usePreviewContent from '@src/hooks/usePreviewContent';
import constructURL from '@src/utils/constructURL';

import { HumanTrakPageClientPropTypes } from './HumanTrakPage.types';
import SocialEmbeds from '@src/components/Shared/SocialEmbeds';
import SubscriptionTable from '@src/components/Shared/SubscriptionTable';

const HumanTrakPageClient: React.FC<HumanTrakPageClientPropTypes> = ({ data }) => {
  // Enable live updates for the post
  const page = usePreviewContent({ content: data?.page });

  // Set the page view path for analytics tracking
  const { setPathPageView, setContentType } = useTrackingCode();
  setPathPageView(
    constructURL({ languageCode: page?.languageCode?.toLowerCase(), slug: `/products/humantrak`, baseURL: '' }),
  );
  setContentType('site-page');

  return (
    <>
      <HeroValdProd
        id={page?.hero?.items?.[0]?.scrollTo?.slice(1)}
        ctfData={page?.hero?.items?.[0]}
        className='3xl:mt-[5rem]! mt-[2.4rem]! mb-[0rem]! min-h-[46rem] overflow-hidden sm:min-h-[53rem] md:min-h-[53rem]! lg:mt-[0]! lg:min-h-[50rem]! lg:pb-[3rem]! 2xl:min-h-[65rem]!'
        imgClassName='sm:absolute! sm:w-[50rem] lg:w-[60rem] 2xl:w-7xl 3xl:w-[100rem] sm:right-[0] top-[5rem] sm:top-[20rem] lg:top-[10rem] 2xl:top-[12rem] 3xl:top-[0]'
        logoClassName='mt-[3rem] md:mt-[5.7rem] max-w-[27.6386rem] mb-[2.4rem] md:max-w-[25.7rem] md:mt-[4.1rem] lg:mt-0 xl:mt-[4rem] lg:max-w-[35.9302rem] 2xl:max-w-[53.96rem]'
        introClassName='[&>p]:tracking-[-0.0342rem] [&>p]:leading-[2.7rem] [&>p]:text-[1.8rem] [&>p]:max-w-[30.5rem] md:[&>p]:text-[2rem] md:[&>p]:leading-[3rem] md:[&>p]:tracking-[-0.036rem] md:[&>p]:max-w-[39.6rem] xl:[&>p]:max-w-[48.5rem] lg:[&>p]:text-[2.4rem] lg:[&>p]:leading-[3.6rem] 2xl:[&>p]:max-w-[58.3rem] [&_a]:text-[1.8rem] md:[&_a]:text-[2rem] lg:[&_a]:text-[2.4rem]'
        headerClassName='md:w-[80%] md:self-start lg:self-center lg:w-[50%] z-100'
      />

      <ImageTextSecondary
        reversed
        id={page?.motionAnalysis?.items?.[0]?.scrollTo?.slice(1)}
        className='overflow-visible! py-[5rem] lg:py-[0]'
        background='bg-background-tertiary'
        title={page?.motionAnalysis?.items?.[0]?.title}
        titleClassName='lg:max-w-[60rem]'
        imgURL={page?.motionAnalysis?.items?.[0]?.mediaPrimary?.url}
        imgAlt={page?.motionAnalysis?.items?.[0]?.mediaPrimary?.description}
        objectFit='contain'
        childrenElements={
          <>
            <RenderBodyCopy
              className='text-left lg:max-w-[78.2rem]'
              bodyCopy={page?.motionAnalysis?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons items={page?.motionAnalysis?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <Container id={page?.humanTrakVideo?.items?.[0]?.scrollTo?.slice(1)} className='mb-[8rem] lg:mb-[12rem]'>
        <SectionHeader
          className='mt-[3rem] justify-center pb-[3.3rem] md:mb-[3.4rem] lg:pb-[unset]'
          childrenClassName='max-w-[111rem]! xl:max-w-[111rem]! w-full'
          pretitle={page?.humanTrakVideo?.items?.[0]?.preTitle}
          title={page?.humanTrakVideo?.items?.[0]?.title}
          titleClassName='text-[2.8rem] xl:text-[4.8rem] mb-[2.4rem] leading-[3.2rem] md:leading-[4.6rem] xl:leading-[5rem] font-light xl:max-w-[45rem] inline-block'
          align='center'
        >
          <>
            <SocialEmbeds
              className='overflow-hidden rounded-[0.6rem] shadow-[0px_5px_15px_rgba(0,0,0,0.35)]'
              data={page?.humanTrakVideo?.items?.[0]?.extraAssetsCollection?.items?.[0]}
            />
          </>
        </SectionHeader>
      </Container>

      <Container id={page?.intuitiveAndPowerful?.items?.[0]?.scrollTo?.slice(1)} className='md:flex md:flex-col'>
        <SectionHeader
          className='justify-center lg:pb-[unset]'
          pretitle={page?.intuitiveAndPowerful?.items?.[0]?.preTitle}
          title={page?.intuitiveAndPowerful?.items?.[0]?.title}
          titleClassName='max-w-[51.6rem] text-[2.8rem] xl:text-[4.8rem] mb-[2.4rem] leading-[3.2rem] md:leading-[4.6rem] xl:leading-[5rem] font-light'
          align='center'
        >
          <>
            <RenderBodyCopy
              className='max-w-[58.2rem] lg:max-w-[78.2rem]'
              bodyCopy={page?.intuitiveAndPowerful?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons
              className='justify-center'
              items={page?.intuitiveAndPowerful?.items?.[0]?.buttonCollection?.items}
            />
          </>
        </SectionHeader>
      </Container>

      <HotSpots data={page?.intuitiveAndPowerful?.items?.[0]} />

      <div id={page?.context?.items?.[0]?.scrollTo?.slice(1)} className='bg-background-tertiary relative'>
        <ImageTextSecondary
          background='lg:ml-[unset]! lg:mr-[unset]! lg:w-full!'
          pretitle={page?.context?.items?.[0]?.preTitle}
          title={page?.context?.items?.[0]?.title}
          titleClassName='lg:max-w-[35rem]'
          imgURL={page?.context?.items?.[0]?.mediaPrimary?.url}
          imgAlt={page?.context?.items?.[0]?.mediaPrimary?.description}
          objectFit='contain'
          childrenElements={
            <>
              <RenderBodyCopy
                className='lg:max-w-[50rem]'
                bodyCopy={page?.context?.items?.[0]?.bodyCopy?.json?.content}
              />
              <RenderButtons className='justify-start' items={page?.context?.items?.[0]?.buttonCollection?.items} />
            </>
          }
        />
      </div>

      <ImageTextSecondary
        reversed
        id={page?.athletes?.items?.[0]?.scrollTo?.slice(1)}
        className='lg:mt-[-6rem]! lg:mb-[0]!'
        background='lg:ml-[unset]! lg:mr-[unset]! lg:w-full!'
        pretitle={page?.athletes?.items?.[0]?.preTitle}
        title={page?.athletes?.items?.[0]?.title}
        titleClassName='lg:max-w-[62rem]'
        imgURL={page?.athletes?.items?.[0]?.mediaPrimary?.url}
        imgAlt={page?.athletes?.items?.[0]?.mediaPrimary?.description}
        imgSecondClassName='relative lg:bottom-[-6rem] lg:left-[-6rem] lg:z-[-1]'
        objectFit='contain'
        childrenElements={
          <>
            <RenderBodyCopy
              className='2xl:max-w-[78.2rem]'
              bodyCopy={page?.athletes?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons items={page?.athletes?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <HumanTrakVideo id={page?.humanTrakKit?.items?.[0]?.scrollTo?.slice(1)} data={page?.humanTrakKit?.items?.[0]} />

      <Container
        id={page?.valdSubscription?.items?.[0]?.scrollTo?.slice(1)}
        background='bg-[#F9F9F9]'
        className='mt-[0]! py-[8rem]'
      >
        <SectionHeader
          className='mb-[2.4rem] lg:mb-[unset]'
          childrenClassName='w-full'
          pretitle={page?.valdSubscription?.items?.[0]?.preTitle}
          title={page?.valdSubscription?.items?.[0]?.title}
          titleClassName='text-[2.8rem] xl:text-[4.8rem] [&>span]:inline-block mb-[2.4rem] leading-[3.2rem] md:leading-[4.6rem] xl:leading-[5rem] font-light xl:max-w-[60rem] inline-block'
          align='center'
        >
          <RenderButtons
            items={page?.valdSubscription?.items?.[0]?.buttonCollection?.items}
            className='mt-[1rem]! justify-center'
          />
        </SectionHeader>
        <SubscriptionTable
          headerImgL='w-[13rem] lg:w-3xs'
          headerImgR='w-[13.5rem] lg:w-3xs'
          data={page?.valdSubscription?.items}
        />
      </Container>

      <FeaturedProducts className='mt-[8rem] [&>header>h2]:max-w-[70rem]' data={page?.valdSystems?.items?.[0]} />

      <CallToAction
        id={page?.callToAction?.items?.[0]?.scrollTo?.slice(1)}
        title={page?.callToAction?.items?.[0]?.title}
        bodyCopy={page?.callToAction?.items?.[0]?.bodyCopy?.json?.content}
        buttons={page?.callToAction?.items?.[0]?.buttonCollection?.items}
      />
    </>
  );
};

export default HumanTrakPageClient;
